import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Copy, Loader2, Wand2, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

const STATUS_OPTIONS = [
  { value: "pendente", label: "Pendente", cls: "bg-stone-100 text-stone-600" },
  { value: "aprovada", label: "Aprovada", cls: "bg-blue-100 text-blue-700" },
  { value: "em_desenvolvimento", label: "Em desenvolvimento", cls: "bg-amber-100 text-amber-700" },
  { value: "implementada", label: "Implementada", cls: "bg-emerald-100 text-emerald-700" },
  { value: "descartada", label: "Descartada", cls: "bg-red-100 text-red-600" },
];

const CAT_CLS = {
  bug: "bg-red-100 text-red-700",
  prompt: "bg-purple-100 text-purple-700",
  automacao: "bg-blue-100 text-blue-700",
  crm: "bg-teal-100 text-teal-700",
  ux: "bg-pink-100 text-pink-700",
  busca: "bg-indigo-100 text-indigo-700",
  funcionalidade: "bg-emerald-100 text-emerald-700",
  outro: "bg-stone-100 text-stone-600",
};

function ImprovementCard({ imp, onChanged }) {
  const [updating, setUpdating] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [showEvidence, setShowEvidence] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);

  const changeStatus = async (status) => {
    setUpdating(true);
    const willGenerate = status === "aprovada" && !imp.generated_prompt;
    if (willGenerate) toast.info("Aprovada! Gerando o prompt técnico, leva uns segundos...");
    try {
      await api.patch(`/admin/audit/improvements/${imp.id}`, { status });
      toast.success(`Status atualizado pra "${STATUS_OPTIONS.find((s) => s.value === status)?.label}"`);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao atualizar status");
    } finally { setUpdating(false); }
  };

  const generatePrompt = async () => {
    setGenerating(true);
    try {
      await api.post(`/admin/audit/improvements/${imp.id}/generate-prompt`);
      toast.success("Prompt técnico gerado");
      onChanged();
    } catch { toast.error("Erro ao gerar prompt"); }
    finally { setGenerating(false); }
  };

  const copyPrompt = () => {
    navigator.clipboard.writeText(imp.generated_prompt || "");
    toast.success("Prompt copiado! Cola na conversa com a Emergent.");
  };

  const statusInfo = STATUS_OPTIONS.find((s) => s.value === imp.status) || STATUS_OPTIONS[0];

  return (
    <Card className="p-4" data-testid={`audit-improvement-card-${imp.id}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="font-semibold text-[#1B3A2F]">{imp.titulo}</span>
            <Badge className={CAT_CLS[imp.categoria] || CAT_CLS.outro}>{imp.categoria}</Badge>
            <Badge variant="outline">impacto {imp.impacto}</Badge>
            <Badge className={statusInfo.cls}>{statusInfo.label}</Badge>
          </div>
          <div className="text-xs text-stone-400 mb-1">{imp.user_name} · auditoria de {imp.audit_date}</div>
          <p className="text-sm text-stone-600 whitespace-pre-wrap">{imp.descricao}</p>

          {(imp.evidencia || []).length > 0 && (
            <button onClick={() => setShowEvidence(!showEvidence)}
              data-testid={`audit-improvement-evidence-toggle-${imp.id}`}
              className="text-xs text-[#D4651A] font-medium mt-2 flex items-center gap-1">
              {showEvidence ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {showEvidence ? "Esconder evidências" : `Ver evidências (${imp.evidencia.length})`}
            </button>
          )}
          {showEvidence && (
            <div className="mt-2 space-y-1.5">
              {imp.evidencia.map((e, i) => (
                <blockquote key={i} className="text-xs text-stone-600 italic border-l-2 border-[#D4651A] bg-orange-50/60 pl-3 pr-2 py-1.5 rounded-r">
                  "{e}"
                </blockquote>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2 items-end">
          <Select value={imp.status} onValueChange={changeStatus} disabled={updating}>
            <SelectTrigger className="w-[190px]" data-testid={`audit-improvement-status-${imp.id}`}>
              {updating ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {imp.status !== "pendente" && imp.status !== "descartada" && !imp.generated_prompt && (
            <Button size="sm" variant="outline" onClick={generatePrompt} disabled={generating}
              data-testid={`audit-improvement-genprompt-${imp.id}`} className="gap-1.5">
              {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />} Gerar prompt
            </Button>
          )}
        </div>
      </div>

      {imp.generated_prompt && (
        <div className="mt-3 border-t pt-3">
          <div className="flex items-center justify-between mb-2">
            <button onClick={() => setShowPrompt(!showPrompt)}
              data-testid={`audit-improvement-prompt-toggle-${imp.id}`}
              className="text-sm font-medium text-[#1B3A2F] flex items-center gap-1">
              {showPrompt ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              Prompt técnico pra Emergent
            </button>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={generatePrompt} disabled={generating} className="gap-1.5"
                data-testid={`audit-improvement-regenprompt-${imp.id}`}>
                {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />} Regenerar
              </Button>
              <Button size="sm" onClick={copyPrompt} data-testid={`audit-improvement-copyprompt-${imp.id}`}
                className="bg-[#1B3A2F] hover:bg-[#0F1F1A] text-white gap-1.5">
                <Copy className="w-3.5 h-3.5" /> Copiar prompt
              </Button>
            </div>
          </div>
          {showPrompt && (
            <pre className="text-xs bg-[#0A1813] text-stone-200 p-4 rounded-lg whitespace-pre-wrap max-h-80 overflow-y-auto"
              data-testid={`audit-improvement-prompt-text-${imp.id}`}>
              {imp.generated_prompt}
            </pre>
          )}
        </div>
      )}
    </Card>
  );
}

export function AdminAuditImprovements({ refreshKey }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [categoria, setCategoria] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (status !== "all") params.set("status", status);
      if (categoria !== "all") params.set("categoria", categoria);
      const res = await api.get(`/admin/audit/improvements?${params}`);
      setItems(res.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [refreshKey, status, categoria]);

  return (
    <div className="space-y-4" data-testid="audit-improvements-tab">
      <div className="flex gap-3 flex-wrap">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[200px]" data-testid="audit-improvements-filter-status">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os status</SelectItem>
            {STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={categoria} onValueChange={setCategoria}>
          <SelectTrigger className="w-[200px]" data-testid="audit-improvements-filter-categoria">
            <SelectValue placeholder="Categoria" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as categorias</SelectItem>
            {Object.keys(CAT_CLS).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="py-12 text-center text-stone-400">Carregando melhorias...</div>
      ) : items.length === 0 ? (
        <Card className="p-8 text-center text-stone-400" data-testid="audit-improvements-empty">
          Nenhuma melhoria registrada ainda. Elas aparecem aqui automaticamente após cada auditoria.
        </Card>
      ) : (
        items.map((imp) => <ImprovementCard key={imp.id} imp={imp} onChanged={load} />)
      )}
    </div>
  );
}
